import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { userService } from '../../_services';
import { Redirect } from "react-router-dom";
import ColorJson from '../../../src/whitelabel.json';
import { cn, safeParseDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import FilterTagCom from '../FilterTag';

// New modern widget components
import BarListChart from '../BarListChart';
import ColumnPieChart from '../ColumnPieChart';
import ListChartWidget from '../ListChartWidget';
import MultiStackBarChart from '../MultiStackBarChart';
import MultiLineChart from '../MultiLineChart';

// Legacy components (to be migrated)
import Claim from '../Claim';

function AdminDashboard({ dashboardName: propDashboardName, startDate: propStartDate, endDate: propEndDate, handeChangefilter, filterUpdateTrigger }) {
    // Calculate default dates
    const getDefaultDates = () => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        const defaultEndDate = new Date();
        return { defaultStartDate, defaultEndDate };
    };

    // Get stored dates from localStorage
    const getStoredDates = () => {
        const { defaultStartDate, defaultEndDate } = getDefaultDates();
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
        
        return { startdate, enddate };
    };

    // Get user role info
    const getUserRoleInfo = () => {
        let roletype = '', rolename = '', whId = 0;
        try {
            const userdata = JSON.parse(localStorage.getItem('user'));
            if (userdata.userrole === "Distributor" && userdata.wholesaler === "Avenel") {
                roletype = "avdistributor";
                rolename = userdata.email;
                whId = userdata.wholesaler_Id;
            } else if (userdata.userrole === "Distributor" && userdata.wholesaler !== "Avenel") {
                roletype = "distributor";
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            } else {
                roletype = userdata.userrole;
                rolename = userdata.paramsname;
                whId = userdata.wholesaler_Id;
            }
        } catch (error) { }
        return { roletype, rolename, whId };
    };

    // Initialize state
    const { startdate, enddate } = getStoredDates();
    const { roletype, rolename, whId } = getUserRoleInfo();
    const _rawFilter = localStorage.getItem('Advance_Filter');
    const filterdatalocal = (_rawFilter && _rawFilter !== 'null' && _rawFilter !== '{}' && _rawFilter !== 'undefined')
        ? _rawFilter
        : null;

    const [state, setState] = useState({
        scrollArray: {},
        graphData: [],
        dashboardName: propDashboardName,
        endDate: format(enddate, "yyyyMMdd"),
        startDate: format(startdate, "yyyyMMdd"),
        FilterTagMatch: [],
        FilterTagList: filterdatalocal,
        roletype: roletype,
        rolename: rolename,
        wh_Id: whId
    });

    // Refs
    const resizeHandlerRef = useRef(null);

    // Get colors based on domain
    const getColors = useCallback(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        const isRxHacker = host === "rxhacker.com" || 
                          host === "localhost:5464" || 
                          window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
                          window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
        
        if (isRxHacker) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                extra: ColorJson.ApplicationColor.Rxhacker_extra_color
            };
        }
        return {
            primary: ColorJson.ApplicationColor.Glic_primary_color,
            secondary: ColorJson.ApplicationColor.Glic_secondary_color,
            extra: ColorJson.ApplicationColor.Glic_extra_color
        };
    }, []);

    // Build graph data
    const buildGraphData = useCallback(() => {
        const APIBaseURL = '/api/admin';
        const wname = "admin";
        const { defaultStartDate, defaultEndDate } = getDefaultDates();
        const startDate = format(safeParseDate(propStartDate, defaultStartDate), "yyyyMMdd");
        const endDate = format(safeParseDate(propEndDate, defaultEndDate), "yyyyMMdd");

        let AdFilterJson = '';
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Advance_Filter'));
        } catch (error) { }

        const filterTagList = (AdFilterJson != null && AdFilterJson !== '' && AdFilterJson !== undefined && AdFilterJson !== "null")
            ? JSON.stringify(AdFilterJson)
            : '';

        const { primary: primaryColor, secondary: secondaryColor, extra: extraColor } = getColors();
        const filterTime = localStorage.getItem('Filter_Last_Update_Time');
        const hdType = localStorage.getItem('Updated_HD_Type');
        const Request_user = "admin";

        // Request JSON builders
        const buildRequestJson = (chartType, rcount = 30, extra = {}) => {
            return JSON.stringify({
                wname: Request_user,
                start_date: startDate,
                end_date: endDate,
                rcount: rcount,
                chart_type: chartType,
                filtertime: filterTime,
                rxsClaimType: hdType,
                ...extra
            });
        };

        const Column_PostData_RequestJson = buildRequestJson("columnList", 30, { ispatientsaving: "true", pharmacynumber: "all" });
        const PostData_RequestJson = buildRequestJson("pielist", 30);
        const PostData_PatientSaving_False = buildRequestJson("columnList", 30, { ispatientsaving: "false", pharmacynumber: "all" });
        const Table_Pharmacy_RequestJson = buildRequestJson("list", 30);
        const TotalTransaction_PostData_RequestJson = buildRequestJson("columnList", 90, { ispatientsaving: "true", pharmacynumber: "all" });
        const Claim_PostData_RequestJson = buildRequestJson("list", 0);
        const MultiLine_PostData_RequestJson = buildRequestJson("multilinelist", 0);

        const graphData = [
            {
                url: APIBaseURL + "/total_transactions_new",
                type: "Bar_List",
                title: "Total Transactions",
                Subtitle: "Total number of transactions for each day",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                interval: 'day',
                id: "WTotalTransaction",
                RequestJson: TotalTransaction_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/ClaimCalculation",
                type: "Claim",
                title: "",
                Subtitle: "",
                ColorCode: extraColor,
                id: "RxSensClaim",
                RequestJson: Claim_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/new_and_repeat_claims",
                type: "MultiLine_List",
                title: "New vs Repeat Transactions",
                Subtitle: "New vs Repeat Compensable Transactions",
                ColorCode: primaryColor,
                interval: 'day',
                XlabelString: "Date",
                YlabelString: "# of Transactions",
                id: "new_repeat_widget",
                BorderRadius: 0,
                RequestJson: MultiLine_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/metric1a_new",
                type: "Bar_List",
                title: "Transactions by Group Number",
                Subtitle: "Transactions for each group number",
                ColorCode: secondaryColor,
                DataType: "GroupNumber",
                XlabelString: "Group Number",
                YlabelString: "# of Transactions",
                id: "WMetric1a",
                RequestJson: Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/patient_savings_new",
                type: "Bar_List",
                title: "Average Patient Savings (%)",
                Subtitle: "Average savings for all patients in %",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Average Savings",
                id: "WMetric3B",
                RequestJson: PostData_PatientSaving_False,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/drugtransaction_new",
                type: "Column_Pie",
                title: "Transactions by Drug",
                Subtitle: "Number of transactions for each drug",
                ColorCode: extraColor,
                DataType: "DrugName",
                id: "WDrugTransaction",
                RequestJson: PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/commission_new",
                type: "Bar_List",
                title: "Total Commission Earned",
                Subtitle: "Commission earned for each month",
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "Date",
                YlabelString: "Commission",
                interval: 'month',
                id: "WCommission",
                RequestJson: Column_PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/whnametransaction_new",
                type: "Column_Pie",
                title: "Transactions by Wholesaler",
                Subtitle: "Number of transactions for each wholesaler",
                ColorCode: extraColor,
                DataType: "WholesalerName",
                id: "whnametransaction_new",
                RequestJson: PostData_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/Repeat_Prescriptions_transaction_new",
                type: "List",
                title: "Transactions by Repeat Prescriptions",
                Subtitle: "Number of repeat prescriptions by each Prescriber",
                ColorCode: extraColor,
                DataType: "PrescriberName",
                XlabelString: "",
                YlabelString: "",
                id: "WRepeat_PrescriptionsTransaction",
                RequestJson: Table_Pharmacy_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ];

        return { graphData, filterTagList, startDate, endDate, dashboardName: wname };
    }, [propStartDate, propEndDate, getColors]);

    // Change data handler
    const changeData = useCallback(() => {
        const result = buildGraphData();
        setState(prev => ({
            ...prev,
            graphData: result.graphData,
            FilterTagList: result.filterTagList,
            startDate: result.startDate,
            endDate: result.endDate,
            dashboardName: result.dashboardName
        }));
    }, [buildGraphData]);

    // Handle filter change
    const handlechangea = useCallback((e) => {
        // If e has heading and value properties, it's from a chart click - we need to process it
        if (e && e.heading && e.value) {
            // Validate heading is not undefined or invalid
            if (e.heading === 'undefined' || !e.heading) {

                return;
            }
            
            // Call the parent's handeChangefilter which will increment filterUpdateTrigger
            // But first, we need to add this filter to localStorage ourselves
            try {
                let Created_Advance_Filter = JSON.parse(localStorage.getItem('Advance_Filter')) || {};
                
                // Add or update the filter
                if (Created_Advance_Filter[e.heading]) {
                    // Check if value already exists
                    if (!Created_Advance_Filter[e.heading].includes(e.value)) {
                        Created_Advance_Filter[e.heading].push(e.value);
                    }
                } else {
                    Created_Advance_Filter[e.heading] = [e.value];
                }
                
                // Save to localStorage
                localStorage.setItem('Advance_Filter', JSON.stringify(Created_Advance_Filter));
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
            } catch (error) {

            }
        } else if (e && e.value) {
            // If heading is missing but we have a value, log a warning

        }
        
        if (handeChangefilter) {
            handeChangefilter(e);
        }
    }, [handeChangefilter]);

    // Handle collapse (kept for backwards compatibility)
    const handleCollapse = useCallback(() => {
        // Sidebar collapse is now handled by the PagesWrapper component
    }, []);

    // Initialize on mount
    useEffect(() => {
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        changeData();

        // Handle responsive layout
        const handleResize = () => {
            const iframes = document.querySelectorAll('.widget-iframe');
            iframes.forEach(iframe => {
                try {
                    const body = iframe.contentDocument?.body;
                    if (body) {
                        if (window.innerWidth < 480) {
                            body.classList.add('scrollMobile');
                        } else {
                            body.classList.remove('scrollMobile');
                        }
                    }
                } catch (e) { /* cross-origin */ }
            });
        };

        const timeoutId = setTimeout(handleResize, 1000);
        window.addEventListener('resize', handleResize);
        resizeHandlerRef.current = handleResize;

        return () => {
            clearTimeout(timeoutId);
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current);
            }
        };
    }, []);

    // Check for filter/date changes and refresh data
    useEffect(() => {
        if (!propStartDate || !propEndDate) return;
        
        const { defaultStartDate, defaultEndDate } = getDefaultDates();
        const startDate = format(safeParseDate(propStartDate, defaultStartDate), "yyyyMMdd");
        const endDate = format(safeParseDate(propEndDate, defaultEndDate), "yyyyMMdd");

        let IsCall = false;
        try {
            const prevFilter = localStorage.getItem('Prev_Filter_Status')?.toLowerCase();
            const updatedFilter = localStorage.getItem('Updated_Filter_Status')?.toLowerCase();
            const prevAdFilter = localStorage.getItem('Prev_AdFilter_Status');
            const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
            const prevHdType = localStorage.getItem('Prev_HD_Type');
            const updatedHdType = localStorage.getItem('Updated_HD_Type');

            if (prevFilter !== updatedFilter || prevAdFilter !== updatedAdFilter || prevHdType !== updatedHdType) {
                IsCall = true;
            }
        } catch (error) {
            IsCall = false;
        }

        if (endDate !== state.endDate || startDate !== state.startDate || IsCall) {
            if (IsCall) {
                localStorage.setItem('Prev_Filter_Status', localStorage.getItem('Updated_Filter_Status'));
                const updatedAdFilter = localStorage.getItem('Updated_AdFilter_Status');
                if (updatedAdFilter === "null" || updatedAdFilter === null) {
                    localStorage.setItem('Prev_AdFilter_Status', null);
                    localStorage.setItem('Updated_AdFilter_Status', null);
                } else {
                    localStorage.setItem('Prev_AdFilter_Status', updatedAdFilter);
                }
                const updatedHdType = localStorage.getItem('Updated_HD_Type');
                if (updatedHdType === "null" || updatedHdType === null) {
                    localStorage.setItem('Prev_HD_Type', null);
                    localStorage.setItem('Updated_HD_Type', null);
                } else {
                    localStorage.setItem('Prev_HD_Type', updatedHdType);
                }
            }
            changeData();
        }
    }, [propStartDate, propEndDate, state.startDate, state.endDate, changeData, filterUpdateTrigger]);

    // Check user permissions
    try {
        const usertype = userService.Get_User_Type();
        if (userService.Check_LoggedIn_Status() === 'Login') {
            return <Redirect to="/login" />;
        }
        if (usertype?.toLowerCase() === 'wholesaler') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'distributor') {
            return <Redirect to='/pageNotFound' />;
        } else if (usertype?.toLowerCase() === 'subadmin') {
            return <Redirect to='/pageNotFound' />;
        }
    } catch (error) { }

    // Widget visibility helpers
    const isFullWidth = (itemId) => {
        const fullWidthTypes = [
            'WTotalTransaction', 'new_repeat_widget', 'RxSensClaim', 'RxSensClaimNew', 
            'whnametransaction_new', 'WRepeat_PrescriptionsTransaction', 'WPharmacyTransaction1', 
            'top100groups', 'WPrescriberTransaction1'
        ];
        return fullWidthTypes.includes(itemId);
    };

    // Render widget content based on type
    const renderWidgetContent = (item, index) => {
        switch (item.type) {
            // Modern widget components
            case "Bar_List":
                return <BarListChart data={item} key={"barlistgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "Column_Pie":
                return <ColumnPieChart data={item} key={"columnpiegraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "List":
                return <ListChartWidget data={item} key={"listgraph" + index} _Index={index} handlechangea={handlechangea} />;
            case "MultiBar_List":
                return <MultiStackBarChart data={item} key={"barlistgraph1" + index} _Index={index} />;
            case "MultiLine_List":
                return <MultiLineChart data={item} key={"multilinelistgraph1" + index} _Index={index} />;
            
            // Legacy components
            case "Claim":
                return <Claim data={item} key={"claimbox" + index} _Index={index} />;
            default:
                return null;
        }
    };

    return (
        <div id="dashboard-container" className="space-y-6">
            {/* Filter Tags */}
            <FilterTagCom 
                Rolename={state.rolename} 
                wh_Id={state.wh_Id} 
                Roletype={state.roletype} 
                df={state.startDate} 
                dt={state.endDate} 
                handlechangea={handlechangea} 
                FilterTags={state.FilterTagList} 
            />

            {/* Dashboard Widgets Grid */}
            <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                {state.graphData.length === 0 ? (
                    Array.from({ length: 9 }).map((_, i) => (
                        <div key={"skeleton-" + i} className="min-h-[420px]">
                            <Skeleton className="h-full w-full rounded-xl" />
                        </div>
                    ))
                ) : (
                    state.graphData.map((item, index) => (
                        <div 
                            key={"graph-widget" + index} 
                            id={item.id + index}
                            className={cn(
                                item.type !== "Claim" && item.type !== "Claim_New" && "min-h-[420px]",
                                isFullWidth(item.id) && "2xl:col-span-2"
                            )}
                        >
                            {renderWidgetContent(item, index)}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default AdminDashboard;
